import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  LANGUAGES,
  LOCALES,
  STRINGS,
  type Language,
  type StringKey,
} from "./strings";

const STORAGE_KEY = "dh_lang";

/** Honour the visitor's own language before falling back to German. */
function detectLanguage(): Language {
  try {
    const stored = localStorage.getItem(STORAGE_KEY) as Language | null;
    if (stored && LANGUAGES.includes(stored)) return stored;
  } catch {
    // Private mode: fine, we just detect.
  }
  for (const tag of navigator.languages ?? []) {
    const code = tag.split("-")[0] as Language;
    if (LANGUAGES.includes(code)) return code;
  }
  return "de";
}

type Translate = (key: StringKey, vars?: Record<string, string | number>) => string;

interface LanguageValue {
  language: Language;
  locale: string;
  setLanguage: (language: Language) => void;
  t: Translate;
}

const LanguageContext = createContext<LanguageValue | null>(null);

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<Language>(detectLanguage);

  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  const setLanguage = useCallback((next: Language) => {
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Not persisting is not worth an error.
    }
    setLanguageState(next);
  }, []);

  const t = useCallback<Translate>(
    (key, vars) => {
      let value: string = STRINGS[language][key] ?? STRINGS.de[key] ?? String(key);
      if (vars) {
        for (const [name, replacement] of Object.entries(vars)) {
          value = value.replace(`{${name}}`, String(replacement));
        }
      }
      return value;
    },
    [language],
  );

  const value = useMemo(
    () => ({ language, locale: LOCALES[language], setLanguage, t }),
    [language, setLanguage, t],
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage(): LanguageValue {
  const context = useContext(LanguageContext);
  if (!context) throw new Error("useLanguage must be used inside LanguageProvider");
  return context;
}
